// Belt-and-braces realtime resilience for the concerts UI (Phase 3 /
// TODO 3.1 + 3.3, Tasks/12a-implementation-todo.md):
//   - reconnect-triggered resync after a long disconnect gap
//   - jittered periodic reconciliation, skipped while the tab is hidden
//   - optional fast cadence while the socket is down (opt-in, see
//     `disconnectedIntervalMs`)
//
// Vote refetches always go through the debounced scheduler so a reconnect
// storm or a synchronised visibility flip cannot stampede GET /api/votes.
// The optional event refetch is a direct GET, so it is additionally bounded
// by MIN_RESYNC_SPACING_MS.

export const RECONCILE_INTERVAL_MS = 90_000;
export const RECONCILE_JITTER_MS = 30_000;
// Floor between two safety-net resyncs. `resyncVotes` is debounced by the
// store, but `refreshEvent` is a direct GET — without this, repeatedly
// backgrounding/foregrounding a tab (or a venue-wide screen wake) fires one
// uncoalesced event request per flip, per client. Overridable via
// `minSpacingMs` (tests inject it rather than importing this constant).
const MIN_RESYNC_SPACING_MS = 5000;

/**
 * @param {object} options
 * @param {() => number|string|null|undefined} options.getEventId
 * @param {(eventId: any) => void} options.resyncVotes
 *   Debounced vote refetch (votesStore.scheduleVotesRefresh). Passed as a
 *   direct function so static analysis can see the store member is used.
 * @param {{ onResyncNeeded: (handler: Function) => () => void }} options.socketStore
 * @param {(eventId: any) => (void|Promise<void>)} [options.refreshEvent]
 *   Optional quiet event refetch (no loading spinner). Invoked alongside the
 *   vote resync after a long disconnect and on the periodic tick.
 * @param {number} [options.intervalMs]
 * @param {number} [options.jitterMs]
 * @param {number|null} [options.disconnectedIntervalMs]
 *   When set, the reconciliation cadence used while `socketStore.isConnected`
 *   is false. Opt-in per view: audience phones (hundreds of clients) must NOT
 *   poll aggressively during a backend restart, but a single always-on
 *   operator display can. Each tick in this mode also nudges the socket to
 *   reconnect immediately rather than waiting out socket.io's backoff.
 * @param {number} [options.minSpacingMs] // floor between two resyncs
 * @param {() => number} [options.now] // injectable for tests
 * @param {() => number} [options.random] // injectable for tests
 * @returns {() => void} stop — cancel timers, listeners, and the resync handler
 */
export function useRealtimeSafetyNet({
  getEventId,
  resyncVotes,
  socketStore,
  refreshEvent,
  intervalMs = RECONCILE_INTERVAL_MS,
  jitterMs = RECONCILE_JITTER_MS,
  disconnectedIntervalMs = null,
  minSpacingMs = MIN_RESYNC_SPACING_MS,
  now = () => Date.now(),
  random = Math.random
} = {}) {
  if (!getEventId || typeof resyncVotes !== "function" || !socketStore) {
    return () => {};
  }

  let timer = null;
  let stopped = false;
  // null (not 0) means "never run" — 0 is a legitimate clock reading.
  let lastResyncAt = null;

  // Opt-in fast mode, only meaningful while the socket is actually down.
  const fastModeEnabled = typeof disconnectedIntervalMs === "number" && disconnectedIntervalMs > 0;
  const isSocketDown = () => fastModeEnabled && socketStore.isConnected === false;

  const runResync = (eventId, { spacingMs = minSpacingMs } = {}) => {
    if (eventId === null || eventId === undefined || eventId === "") {
      return;
    }
    const at = now();
    if (lastResyncAt !== null && at - lastResyncAt < spacingMs) {
      return;
    }
    lastResyncAt = at;
    resyncVotes(eventId);
    if (typeof refreshEvent === "function") {
      Promise.resolve(refreshEvent(eventId)).catch((error) => {
        console.error("Realtime safety-net event refresh failed:", error);
      });
    }
  };

  const unsubscribeResync = socketStore.onResyncNeeded((eventId) => {
    const current = getEventId();
    if (current === null || current === undefined) {
      return;
    }
    if (String(current) !== String(eventId)) {
      return;
    }
    runResync(eventId);
  });

  const nextDelay = () => {
    if (isSocketDown()) {
      // The down-socket cadence still gets ±25% jitter so a fleet of displays
      // recovering from the same outage does not land in lockstep.
      const spread = disconnectedIntervalMs * 0.25;
      return Math.max(0, disconnectedIntervalMs + (random() * 2 - 1) * spread);
    }
    // 90s ± 30s per client — without jitter every open LED/voting tab would
    // hit the API on the same wall-clock cadence.
    const jitter = (random() * 2 - 1) * jitterMs;
    return Math.max(0, intervalMs + jitter);
  };

  const scheduleNext = () => {
    if (stopped) {
      return;
    }
    timer = setTimeout(tick, nextDelay());
  };

  const tick = () => {
    timer = null;
    if (stopped) {
      return;
    }
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      scheduleNext();
      return;
    }
    if (isSocketDown()) {
      // Don't just poll around a dead socket — try to bring it back first.
      socketStore.forceReconnect?.();
      // The min-spacing floor exists to coalesce bursty triggers, not to cap
      // a deliberately fast cadence, so relax it to the configured interval.
      runResync(getEventId(), { spacingMs: Math.min(minSpacingMs, disconnectedIntervalMs) });
    } else {
      runResync(getEventId());
    }
    scheduleNext();
  };

  const onVisibilityChange = () => {
    if (stopped) {
      return;
    }
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      runResync(getEventId());
    }
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }
  scheduleNext();

  return () => {
    stopped = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
    unsubscribeResync();
  };
}
